/**
 * What this organization has sent this month, against its send limit.
 *
 * Before this, the only place a client could learn a limit existed was the
 * refusal: a campaign that did not fit failed with "this campaign reaches 40,000
 * recipients and 5,188 of the monthly limit of 50,000 remain". Every figure in
 * that sentence is here, before the campaign rather than after it.
 *
 * THE HEADLINE IS THE FIGURE THE LIMIT ENFORCES, AND THE HISTORY IS NOT. The
 * headline counts every message queued this month; the history is the hourly
 * rollup of what a provider accepted. They differ by the mail that never left.
 * That is deliberate on the server and said on the screen, because the first
 * thing anybody does with two numbers is compare them.
 *
 * EVERY DATE IS FORMATTED IN UTC. A period is a calendar month in UTC, and
 * midnight on the 1st rendered in local time is the previous evening anywhere
 * west of Greenwich — so the screen would say the allowance resets a day before
 * the refusal says it does. The zone is what has to match; the locale's order
 * of day and month follows the rest of the dashboard.
 */

import { api } from "../api/client";
import { Progress } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useAsync } from "../hooks";
import { useSession } from "../session";
import type { UsagePeriod, UsageReport, UsageWarmup } from "../api/types";

/** From here the bar turns amber. Nothing is refused until it is full. */
const NEAR_LIMIT = 0.9;

const dayAndMonth = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  timeZone: "UTC",
});
const monthAndYear = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

export function Usage() {
  const { orgId } = useSession();
  const report = useAsync<UsageReport>(
    (signal) => api.get<UsageReport>("/v1/usage", { signal }),
    [orgId],
  );

  return (
    <>
      <div className="oc-panel">
        <div className="oc-panel__header row row--between">
          <div>
            <div className="oc-headline-md">This month</div>
            <div className="oc-body-sm oc-text-secondary">
              Every message queued since the first of the month (UTC) — campaigns and transactional
              mail alike.
            </div>
          </div>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={report.reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>

        <div className="oc-panel__body" style={{ display: "grid", gap: 12 }}>
          {report.loading && !report.data && (
            <div className="oc-body-sm oc-text-muted-aa">Loading…</div>
          )}
          {report.error && (
            <p className="field-error" role="alert">
              {report.error.message}
            </p>
          )}
          {report.data && <ThisMonth report={report.data} />}
        </div>
      </div>

      {report.data?.warmup && <Warmup warmup={report.data.warmup} />}

      {report.data && <ByMonth periods={report.data.history} />}
    </>
  );
}

/**
 * Today's warm-up limit, when one is running.
 *
 * The same three figures the refusal names — what is allowed, what is left,
 * what tomorrow allows — so somebody planning today's send has them before the
 * campaign rather than from its failure.
 */
function Warmup({ warmup }: { warmup: UsageWarmup }) {
  const share = warmup.used_today / warmup.limit_today;
  const state = warmup.remaining_today === 0 ? "exhausted" : share >= NEAR_LIMIT ? "near" : "ok";

  return (
    <div className="oc-panel">
      <div className="oc-panel__header">
        <div>
          <div className="oc-headline-md">
            Warm-up, day {warmup.day} of {warmup.days}
          </div>
          <div className="oc-body-sm oc-text-secondary">
            A daily limit on campaign emails that grows each day while your sending domain builds a
            reputation. Transactional mail is not counted.
          </div>
        </div>
      </div>
      <div className="oc-panel__body" style={{ display: "grid", gap: 12 }}>
        <div className="usage-figure">
          <span className="oc-headline-lg">{warmup.used_today.toLocaleString()}</span>
          <span className="oc-body-sm oc-text-secondary">
            of {warmup.limit_today.toLocaleString()} campaign emails today
          </span>
        </div>

        <div className="usage-progress" data-state={state}>
          <Progress value={share * 100} label="Share of today's warm-up limit used" />
        </div>

        <div className="row row--between oc-body-sm">
          <span>
            {warmup.remaining_today === 0
              ? "None remaining today"
              : `${warmup.remaining_today.toLocaleString()} remaining today`}
          </span>
          <span className="oc-text-secondary">
            {warmup.limit_tomorrow
              ? `Tomorrow: ${warmup.limit_tomorrow.toLocaleString()}`
              : "From tomorrow: no daily limit"}
          </span>
        </div>

        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          A campaign larger than what remains today is refused before anything is sent. Send to the
          people who opened or clicked recently first. The warm-up is set by whoever runs this
          deployment.
        </p>
      </div>
    </div>
  );
}

function ThisMonth({ report }: { report: UsageReport }) {
  const used = report.messages_enqueued;
  const noun = used === 1 ? "message" : "messages";

  // No limit: the default on every deployment that has not set one, so this is
  // the common case rather than an edge. A count, and a sentence saying why
  // there is no bar — never an empty state, because nothing is missing.
  if (report.limit === null || report.remaining === null) {
    return (
      <>
        <div className="usage-figure">
          <span className="oc-headline-lg">{used.toLocaleString()}</span>
          <span className="oc-body-sm oc-text-secondary">{noun} this month</span>
        </div>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          {/* Not "nothing is refused" while a warm-up runs: the warm-up below
              refuses a campaign larger than what is left of the day. */}
          {report.warmup
            ? "No monthly limit is set for this organization. The warm-up below still limits campaigns each day."
            : "No send limit is set for this organization, so nothing is refused for volume."}
        </p>
      </>
    );
  }

  const share = used / report.limit;
  const state = report.remaining === 0 ? "exhausted" : share >= NEAR_LIMIT ? "near" : "ok";

  return (
    <>
      <div className="usage-figure">
        <span className="oc-headline-lg">{used.toLocaleString()}</span>
        <span className="oc-body-sm oc-text-secondary">
          of {report.limit.toLocaleString()} {noun} this month
        </span>
      </div>

      <div className="usage-progress" data-state={state}>
        <Progress value={share * 100} label="Share of this month's send limit used" />
      </div>

      <div className="row row--between oc-body-sm">
        <span>
          {report.remaining === 0
            ? "None remaining"
            : `${report.remaining.toLocaleString()} remaining`}
        </span>
        <span className="oc-text-secondary">
          Resets {dayAndMonth.format(new Date(report.period_end))}
        </span>
      </div>

      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        {state === "exhausted"
          ? "Campaigns are refused until the allowance resets. Transactional mail — receipts, password resets — still goes out."
          : "A campaign larger than what remains is refused before anything is sent. Transactional mail is never refused, though it counts toward the limit."}{" "}
        The limit is set by whoever runs this deployment.
      </p>
    </>
  );
}

/**
 * The rollup, one row per month.
 *
 * Labelled by what it counts rather than as "sent", because it is lower than the
 * headline and the heading is the only thing that makes that legible rather
 * than mysterious.
 */
function ByMonth({ periods }: { periods: UsagePeriod[] }) {
  return (
    <div className="oc-panel">
      <div className="oc-panel__header">
        <div className="oc-headline-md">By month</div>
        <div className="oc-body-sm oc-text-secondary">
          What a provider accepted each month. It runs lower than the figure above by any mail that
          failed or was refused: the limit counts a message the moment it is queued, and this counts
          it once a provider takes it.
        </div>
      </div>

      {periods.length === 0 ? (
        <div className="oc-panel__body">
          <div className="oc-empty">
            <div className="oc-empty__title">No monthly figures yet</div>
            <div className="oc-empty__hint">
              Monthly figures are recorded hourly, and none have been recorded for this organization
              so far.
            </div>
          </div>
        </div>
      ) : (
        <div className="oc-table usage-table">
          <div className="oc-table__head" role="row">
            <span>Month</span>
            <span className="oc-table__cell--right">Accepted by a provider</span>
            <span className="oc-table__cell--right">Contacts (peak)</span>
            <span>Status</span>
          </div>
          <div className="oc-table__body">
            {periods.map((p) => (
              <div key={p.period_start} className="oc-table__row" role="row">
                <span>{monthAndYear.format(new Date(p.period_start))}</span>
                <span className="oc-table__cell--right">{p.messages_sent.toLocaleString()}</span>
                <span className="oc-table__cell--right">{p.contacts_peak.toLocaleString()}</span>
                {/* Final is the fact worth stating: a closed month is never
                    recomputed, so its figures are the ones an invoice uses. */}
                <span className={p.closed ? "oc-text-secondary" : undefined}>
                  {p.closed ? "Final" : "In progress"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
